import { Component, HostListener, Input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { AiService } from 'app/services';
import { marked } from 'marked';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
}

@Component({
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
})
export class ChatWindowComponent {
  @Input()
  public isChatOpen = false;
  public prompt = '';
  public isSending = false;
  public errorMessage = '';
  public messages: ChatMessage[] = [];
  public chatWidth = 560;
  public chatHeight = 620;

  private readonly minChatWidth = 320;
  private readonly minChatHeight = 320;
  private readonly horizontalOffset = 24;
  private readonly verticalOffset = 110;
  private isResizing = false;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;

  constructor(private aiService: AiService, private sanitizer: DomSanitizer) {}

  public toggleChat() {
    this.isChatOpen = !this.isChatOpen;
  }

  public sendPrompt() {
    const trimmedPrompt = this.prompt.trim();
    if (!trimmedPrompt || this.isSending) {
      return;
    }

    this.messages.push({ role: 'user', text: trimmedPrompt });
    this.prompt = '';
    this.errorMessage = '';
    this.isSending = true;

    this.aiService.sendPrompt(trimmedPrompt).subscribe({
      next: (response) => {
        this.messages.push({
          role: 'assistant',
          text: response && response.trim() ? response : '(No response)',
        });
        this.isSending = false;
      },
      error: () => {
        this.errorMessage = 'Unable to reach chat service.';
        this.isSending = false;
      },
    });
  }

  public renderMarkdown(content: string): SafeHtml {
    if (!content) {
      return '';
    }
    const html = marked.parse(content, { breaks: true, gfm: true });
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  public startResize(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = this.chatWidth;
    this.startHeight = this.chatHeight;
  }

  @HostListener('document:mousemove', ['$event'])
  public onMouseMove(event: MouseEvent) {
    if (!this.isResizing) {
      return;
    }

    const deltaX = this.startX - event.clientX;
    const deltaY = this.startY - event.clientY;
    const maxWidth = Math.max(
      this.minChatWidth,
      window.innerWidth - this.horizontalOffset
    );
    const maxHeight = Math.max(
      this.minChatHeight,
      window.innerHeight - this.verticalOffset
    );

    this.chatWidth = this.clamp(
      this.startWidth + deltaX,
      this.minChatWidth,
      maxWidth
    );
    this.chatHeight = this.clamp(
      this.startHeight + deltaY,
      this.minChatHeight,
      maxHeight
    );
  }

  @HostListener('document:mouseup')
  public onMouseUp() {
    this.isResizing = false;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
